import { useEffect, useMemo, useRef, useState } from 'react';
import { UserActivity } from 'types/auth';
import useFirebase from './useFirebase';

/**
 * How long without a heartbeat before a user is presented as offline.
 *
 * The native app writes `user-activity/{uid}` every 2 minutes — from the
 * foreground service while the app is open, and from the background tracking
 * isolate while live tracking is on. Three missed beats is the tolerance:
 * enough to absorb OS timer throttling and a tunnel, short enough that a dead
 * phone is called out quickly.
 *
 * The native app uses the SAME window (`kPresenceStaleAfter` in
 * `quik-connect-v2/lib/services/activity_ping_service.dart`). Keep them in step:
 * a driver must not read "Online" on one surface and "Offline" on the other.
 */
export const PRESENCE_STALE_MS = 6 * 60 * 1000;

/** How often staleness is re-evaluated. See the note in usePresence. */
const TICK_MS = 30 * 1000;

export interface Presence {
  /** Epoch ms of the last heartbeat, or null if there has never been one. */
  lastSeenAt: number | null;
  /** Battery percentage at that heartbeat, or null. */
  battery: number | null;
  /** Epoch ms the user was marked logged out, or null. */
  loggedOutAt: number | null;
  /**
   * The app state reported with the last heartbeat (see UserActivity.appState),
   * or null for pre-field app builds. Exposed so a caller can tell a
   * backgrounded-idle driver from a genuinely dark one if it wants to.
   */
  appState: 'foreground' | 'tracking' | 'background' | null;
  /**
   * Online iff a heartbeat landed within PRESENCE_STALE_MS, the user has not
   * since logged out, AND the device is not merely a backgrounded-idle app
   * (appState 'background'). A backgrounded app that is still live-tracking
   * ('tracking') counts as online. False also covers "never seen".
   */
  online: boolean;
}

export type PresenceMap = Record<string, Presence>;

/**
 * Live online/offline state for a set of users.
 *
 * Two things this has to get right, both of which the previous one-shot REST
 * lookup got wrong:
 *
 *  1. **It must update.** `lastSeenAt` came from a single `getLastLoginBatch`
 *     call at page load and was never refreshed, so every driver silently
 *     decayed to "Inactive" a few minutes after the page opened, regardless of
 *     what their phone was doing. Here the heartbeat is streamed from RTDB.
 *
 *  2. **It must expire on a clock.** Going offline is the ABSENCE of an event —
 *     a phone that dies sends nothing — so no subscription will ever fire to
 *     tell us about it. Without the ticker a driver stays "Online" on screen
 *     forever after their device goes dark. The tick only re-renders when a
 *     boundary was actually crossed.
 *
 * `seed` lets a caller pass the REST snapshot it already has (from
 * getLastLoginBatch, which merges the same node server-side) so the indicator is
 * correct on the first paint instead of flashing "Offline" until RTDB attaches.
 */
export function usePresence(
  uids: string[],
  seed?: Record<string, { lastSeenAt?: number | null; battery?: number | null; loggedOutAt?: number | null; appState?: Presence['appState'] }>
): PresenceMap {
  const { subscribeToUserActivity, firebaseUid } = useFirebase();
  const [activity, setActivity] = useState<Record<string, UserActivity>>({});
  const [, setTick] = useState(0);

  const uidsKey = useMemo(() => [...new Set(uids.filter(Boolean))].sort().join(','), [uids]);

  useEffect(() => {
    // RTDB rules gate `user-activity` reads on auth != null — attaching before
    // the custom-token sign-in resolves is a guaranteed permission error.
    if (!firebaseUid || !uidsKey) return;
    const unsubscribe = subscribeToUserActivity(uidsKey.split(','), setActivity);
    return () => unsubscribe();
  }, [firebaseUid, uidsKey, subscribeToUserActivity]);

  // Re-render on a clock so staleness expires. Cheap: one setState every 30 s,
  // and only when at least one user actually crossed the threshold.
  const onlineSigRef = useRef('');
  useEffect(() => {
    const evaluate = () => {
      const now = Date.now();
      const sig = uidsKey
        .split(',')
        .map((uid) => {
          // Staleness re-render only: RTDB wins when present, seed fills the gap
          // before it attaches. Logout is event-driven (the subscription fires),
          // so loggedOutAt isn't needed here — only the time-based boundary is.
          const seen = activity[uid]?.lastSeenAt ?? seed?.[uid]?.lastSeenAt ?? null;
          return `${uid}:${seen != null && now - seen < PRESENCE_STALE_MS ? 1 : 0}`;
        })
        .join(',');
      if (sig !== onlineSigRef.current) {
        onlineSigRef.current = sig;
        setTick((t) => t + 1);
      }
    };
    evaluate();
    const id = setInterval(evaluate, TICK_MS);
    return () => clearInterval(id);
    // `seed` is intentionally read through the closure rather than depended on:
    // it is a fresh object identity on every parent render and would restart the
    // interval each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uidsKey, activity]);

  return useMemo(() => {
    const now = Date.now();
    const out: PresenceMap = {};
    for (const uid of uidsKey ? uidsKey.split(',') : []) {
      // lastSeenAt + battery keep the per-field fallback: RTDB wins when present,
      // the REST seed fills the gap before the subscription attaches (or when the
      // read is denied), so a last-known value survives a heartbeat that omits it.
      const lastSeenAt = activity[uid]?.lastSeenAt ?? seed?.[uid]?.lastSeenAt ?? null;
      const battery = activity[uid]?.battery ?? seed?.[uid]?.battery ?? null;
      // loggedOutAt is the exception — it uses live-wins: once the subscription
      // reports (null after a re-login heartbeat clears the marker), trust it over
      // a stale seed so a driver who logged back in while this page was open reads
      // online again instead of staying stuck offline.
      const live = activity[uid];
      const loggedOutAt = live ? live.loggedOutAt : seed?.[uid]?.loggedOutAt ?? null;
      // A logout at/after the last heartbeat forces offline immediately, WITHOUT
      // touching lastSeenAt/battery — so the row still shows "last seen" + battery
      // for the logged-out driver. Otherwise online is the usual staleness check.
      const loggedOut = loggedOutAt != null && (lastSeenAt == null || loggedOutAt >= lastSeenAt);
      // Why the device is reachable. RTDB wins when present; the seed fills the
      // gap before it attaches. null (old app builds) is NOT 'background', so it
      // stays online as before — this never flips an old client offline.
      const appState = activity[uid]?.appState ?? seed?.[uid]?.appState ?? null;
      // A backgrounded-idle app is reachable but nobody is working — show it
      // offline even though the heartbeat is fresh. 'tracking' and 'foreground'
      // (and absent) remain online.
      const backgroundedIdle = appState === 'background';
      const online = lastSeenAt != null && now - lastSeenAt < PRESENCE_STALE_MS && !loggedOut && !backgroundedIdle;
      out[uid] = {
        lastSeenAt,
        battery,
        loggedOutAt,
        appState,
        online
      };
    }
    return out;
    // Recomputed on every tick via the state bump above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uidsKey, activity, seed, onlineSigRef.current]);
}

export default usePresence;
